import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Runs a cart mutation for the bulb add-on (snippets/bulb-addon.liquid) and hands the
 * re-rendered cart sections to cart-items-component, the same way product-form.js does,
 * so the drawer and cart page morph in place.
 * @param {HTMLElement} source - element the cart event is dispatched from
 * @param {{ action: 'add' | 'remove', url: string, payload: object, variantId: string, quantity: number }} options
 */
async function mutateCart(source, { action, url, payload, variantId, quantity }) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(
      url,
      fetchConfig('json', { body: JSON.stringify({ ...payload, sections: sectionIds.join(',') }) })
    ).then((res) => res.json());
    if (response.status) throw new Error(response.description || response.message);

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
      (res) => res.json()
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bulb-addon',
        sections: response.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Cart update failed',
        code: 'INVALID',
      })
    );
    throw error;
  }
}

/** Cart suggestion: adds one bulb. */
class BulbUpsell extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#add);
  }

  disconnectedCallback() {
    this.querySelector('button')?.removeEventListener('click', this.#add);
  }

  #add = async () => {
    const button = this.querySelector('button');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const variantId = this.dataset.variantId || '';
    try {
      await mutateCart(this, {
        action: 'add',
        url: Theme.routes.cart_add_url,
        payload: { items: [{ id: Number(variantId), quantity: 1 }] },
        variantId,
        quantity: 1,
      });
    } catch {
      button.removeAttribute('aria-busy');
    }
  };
}

/**
 * Rendered only when the cart holds bulbs and nothing else: the bulb is not sold on
 * its own, so it is removed as soon as the last lamp leaves the cart.
 */
class BulbOrphanCleanup extends HTMLElement {
  connectedCallback() {
    const variantId = this.dataset.variantId || '';
    if (!variantId) return;

    mutateCart(this, {
      action: 'remove',
      url: Theme.routes.cart_update_url + '.js',
      payload: { updates: { [variantId]: 0 } },
      variantId,
      quantity: 0,
    }).catch((error) => console.warn('[bulb-addon] Could not remove the bulb:', error));
  }
}

if (!customElements.get('bulb-upsell')) {
  customElements.define('bulb-upsell', BulbUpsell);
}
if (!customElements.get('bulb-orphan-cleanup')) {
  customElements.define('bulb-orphan-cleanup', BulbOrphanCleanup);
}
